import { Image } from "expo-image";
import React from "react";
import { Pressable, View } from "react-native";

import { InsetModal } from "./ui/InsetModal";
import { Text } from "./ui/BrandText";
import { Ionicons } from "./ui/ionicons";
import { border, surface, text as uiText } from "../theme/tokens";

type Props = {
  visible: boolean;
  imageUrls: string[];
  initialIndex?: number;
  onClose: () => void;
};

export function ImageViewerModal({ visible, imageUrls, initialIndex = 0, onClose }: Props) {
  const [currentIndex, setCurrentIndex] = React.useState(initialIndex);
  const [failed, setFailed] = React.useState(false);
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < imageUrls.length - 1;

  React.useEffect(() => {
    if (visible) setCurrentIndex(Math.min(Math.max(initialIndex, 0), Math.max(0, imageUrls.length - 1)));
  }, [imageUrls.length, initialIndex, visible]);
  React.useEffect(() => setFailed(false), [currentIndex]);

  React.useEffect(() => {
    if (!visible) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft" && canGoPrev) {
        event.preventDefault();
        setCurrentIndex((value) => value - 1);
      } else if (event.key === "ArrowRight" && canGoNext) {
        event.preventDefault();
        setCurrentIndex((value) => value + 1);
      }
    };
    document.addEventListener("keydown", handleKey, true);
    return () => document.removeEventListener("keydown", handleKey, true);
  }, [canGoNext, canGoPrev, visible]);

  return (
    <InsetModal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        onPress={onClose}
        accessibilityLabel="Fermer la visionneuse d'images"
        style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.92)", padding: 16, alignItems: "center", justifyContent: "center" }}
      >
        <View
          onStartShouldSetResponder={() => true}
          style={{ width: "100%", height: "100%", maxWidth: 1440, alignItems: "center", justifyContent: "center" }}
        >
          <Pressable
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Fermer la visionneuse"
            style={{ position: "absolute", top: 0, right: 0, width: 48, height: 48, borderRadius: 24, backgroundColor: surface.elevated, alignItems: "center", justifyContent: "center", zIndex: 3 }}
          >
            <Ionicons name="close" size={24} color={uiText.primary} />
          </Pressable>
          {imageUrls.length > 1 ? (
            <Text accessibilityLiveRegion="polite" style={{ position: "absolute", top: 12, color: uiText.primary, fontWeight: "800", zIndex: 2 }}>
              {currentIndex + 1} / {imageUrls.length}
            </Text>
          ) : null}
          {canGoPrev ? (
            <Pressable onPress={() => setCurrentIndex((value) => value - 1)} accessibilityRole="button" accessibilityLabel="Image précédente" style={{ position: "absolute", left: 0, width: 48, height: 48, borderRadius: 24, backgroundColor: surface.elevated, alignItems: "center", justifyContent: "center", zIndex: 3 }}>
              <Ionicons name="chevron-back" size={24} color={uiText.primary} />
            </Pressable>
          ) : null}
          {canGoNext ? (
            <Pressable onPress={() => setCurrentIndex((value) => value + 1)} accessibilityRole="button" accessibilityLabel="Image suivante" style={{ position: "absolute", right: 0, width: 48, height: 48, borderRadius: 24, backgroundColor: surface.elevated, alignItems: "center", justifyContent: "center", zIndex: 3 }}>
              <Ionicons name="chevron-forward" size={24} color={uiText.primary} />
            </Pressable>
          ) : null}
          {failed ? (
            <View style={{ padding: 24, borderRadius: 16, borderWidth: 1, borderColor: border.subtle, backgroundColor: surface.elevated }}>
              <Text style={{ color: uiText.primary }}>Cette image est indisponible.</Text>
            </View>
          ) : imageUrls[currentIndex] ? (
            <Image
              source={{ uri: imageUrls[currentIndex] }}
              cachePolicy="disk"
              contentFit="contain"
              accessibilityLabel={`Image ${currentIndex + 1} sur ${imageUrls.length} de la publication`}
              onError={() => setFailed(true)}
              style={{ width: "90%", height: "85%" }}
            />
          ) : null}
        </View>
      </Pressable>
    </InsetModal>
  );
}
